import React, { useEffect, useMemo } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ConfigProvider, message } from '@/shared/antd-imports';
import { App as AntdApp, notification } from 'antd';

import { store } from '@/store';
import { useAppSelector } from '@/hooks/useAppSelector';
import { socketManager } from '@/utils/socket';
import { getThemeConfig } from '@/config/theme.config';
import { AppRoutes } from '@/config/routes.config';
import AuthProvider from '@/components/AuthProvider';

const AppContent: React.FC = () => {
  const { theme: currentTheme } = useAppSelector((state) => state.ui);
  const themeConfig = useMemo(() => getThemeConfig(currentTheme), [currentTheme]);

  // Static message / notification / Modal.confirm calls render outside this tree (they show since the React 19 patch
  // in main.tsx), so they need the theme through holderRender or they stay light in dark mode. config({}) re-syncs
  // the global holders after a theme switch.
  useEffect(() => {
    ConfigProvider.config({
      holderRender: (children) => <ConfigProvider theme={themeConfig}>{children}</ConfigProvider>,
    });
    message.config({});
    notification.config({});
  }, [themeConfig]);

  useEffect(() => {
    return () => {
      socketManager.disconnect();
    };
  }, []);

  // ANS-57: the page behind the layout (overscroll on phones, short pages) follows the theme too.
  useEffect(() => {
    const background = getThemeConfig(currentTheme).token?.colorBgLayout;
    if (background) document.body.style.backgroundColor = String(background);
    document.documentElement.style.colorScheme = currentTheme === 'dark' ? 'dark' : 'light';
  }, [currentTheme]);

  return (
    <ConfigProvider theme={themeConfig}>
      <AntdApp>
        <AuthProvider>
          <Router>
            <AppRoutes />
          </Router>
        </AuthProvider>
      </AntdApp>
    </ConfigProvider>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
};

export default App;
